import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Building, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIERS = [
  { value: 'starter', label: 'Starter', color: 'bg-[hsl(var(--info))] text-white' },
  { value: 'professional', label: 'Professional', color: 'bg-[hsl(var(--accent))] text-white' },
  { value: 'enterprise', label: 'Enterprise', color: 'bg-[hsl(var(--success))] text-white' }
];

export default function AdminCompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subscription_tier: 'starter'
  });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchCompanies();
  }, [page]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/companies?skip=${page * limit}&limit=${limit}`, {
        withCredentials: true
      });
      if (response.data.companies) {
        setCompanies(response.data.companies);
        setTotal(response.data.total);
      } else {
        // Backward compatibility if backend doesn't return paginated response
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('[AdminCompanies] Error fetching:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCompany(null);
    setFormData({ name: '', description: '', subscription_tier: 'starter' });
    setShowDialog(true);
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      description: company.description || '',
      subscription_tier: company.subscription_tier
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingCompany) {
        await axios.patch(
          `${BACKEND_URL}/api/admin/companies/${editingCompany.id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Company updated successfully!');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/companies`,
          formData,
          { withCredentials: true }
        );
        toast.success('Company created successfully!');
      }
      setShowDialog(false);
      await fetchCompanies();
    } catch (error) {
      console.error('[AdminCompanies] Error saving:', error);
      toast.error('Failed to save company');
    }
  };

  const getTierBadge = (tier) => {
    const tierConfig = TIERS.find(t => t.value === tier) || TIERS[0];
    return <Badge className={tierConfig.color}>{tierConfig.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <Building className="h-12 w-12 animate-pulse text-[hsl(var(--muted-foreground))]" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="admin-companies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
            Company Management
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-1">Manage companies and subscriptions</p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
          data-testid="create-company-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Company
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {companies.map((company) => (
          <Card key={company.id} data-testid={`company-${company.id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">{company.name}</CardTitle>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                    {company.user_count || 0} users
                  </p>
                </div>
                {getTierBadge(company.subscription_tier)}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {company.description && (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  {company.description}
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleEdit(company)}
                  data-testid={`edit-company-${company.id}`}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Pagination Controls */}
      {total > 0 && (
        <Card className="mt-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-[hsl(var(--muted-foreground))]">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} companies
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  data-testid="companies-prev-page"
                >
                  Previous
                </Button>
                <div className="flex items-center gap-2 px-3">
                  <span className="text-sm">Page {page + 1} of {Math.ceil(total / limit)}</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(page + 1)}
                  disabled={(page + 1) * limit >= total}
                  data-testid="companies-next-page"
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="company-dialog">
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Edit Company' : 'Create Company'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter company name"
                data-testid="company-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Company description (optional)"
                data-testid="company-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Subscription Tier</Label>
              <select
                className="w-full p-2 border rounded-md bg-[hsl(var(--background))] text-[hsl(var(--foreground))]"
                value={formData.subscription_tier}
                onChange={(e) => setFormData({ ...formData, subscription_tier: e.target.value })}
                data-testid="tier-select"
              >
                {TIERS.map(tier => (
                  <option key={tier.value} value={tier.value}>{tier.label}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSave}
              className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
              data-testid="save-company-btn"
            >
              {editingCompany ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
